/**
 * Feature gate definitions — what each tier unlocks.
 * Consumed by SubscriptionContext; never knows about payment provider.
 */

const TIER_GATES = {
  free: {
    dailySessions: Infinity,
    plantVarieties: ['roma', 'cherry'],
    calendarView: false,
    detailedAnalytics: false,
    ambientSounds: false,
    realFarmCounter: false,
  },
  premium: {
    dailySessions: Infinity,
    plantVarieties: 'all',
    calendarView: true,
    detailedAnalytics: true,
    ambientSounds: true,
    realFarmCounter: true,
  },
};

/**
 * Check if a subscription tier has access to a boolean feature.
 * @param {{ tier: string }} subscription
 * @param {string} feature
 * @returns {boolean}
 */
export function canAccess(subscription, feature) {
  const tier = subscription?.tier || 'free';
  const gates = TIER_GATES[tier] || TIER_GATES.free;
  const value = gates[feature];
  if (typeof value === 'boolean') return value;
  // For array features like plantVarieties, "all" means true
  if (value === 'all') return true;
  // For numeric limits, having any quota counts as access
  if (typeof value === 'number') return value > 0;
  return false;
}

/**
 * Get the limit for a numeric or array feature.
 * @param {{ tier: string }} subscription
 * @param {string} feature
 * @returns {number|string[]|string}
 */
export function getLimit(subscription, feature) {
  const tier = subscription?.tier || 'free';
  const gates = TIER_GATES[tier] || TIER_GATES.free;
  return gates[feature];
}

/**
 * Check if a specific plant variety is available for the tier.
 * @param {{ tier: string }} subscription
 * @param {string} species
 * @returns {boolean}
 */
export function isPlantUnlocked(subscription, species) {
  const allowed = getLimit(subscription, 'plantVarieties');
  if (allowed === 'all') return true;
  return Array.isArray(allowed) && allowed.includes(species);
}

export { TIER_GATES };
